import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { MemoryManager } from '@/services/MemoryManager';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, AreaChart, BarChart, Bar, ReferenceArea } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendingUp, TrendingDown, Target, ZoomOut, RotateCcw, DollarSign, Activity, Sparkles, BarChart3, LineChart as LineChartIcon, Volume2, Maximize2, ZoomIn } from 'lucide-react';
import { CanvasEquityChart } from '@/components/admin/merge-bots/CanvasEquityChart';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import { GrowthDataPoint, GrowthStats } from '@/services/prop-bot/prop-bot-growth-service';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface PropBotGrowthChartProps {
  originalData: GrowthDataPoint[];
  shadowData: GrowthDataPoint[];
  originalStats: GrowthStats;
  shadowStats: GrowthStats;
  linkedBotType: 'original' | 'shadow' | null;
  minCapital: number;
  isSimulatedData?: boolean;
  isMerged?: boolean;
  mergedData?: {
    growthData: GrowthDataPoint[];
    stats: GrowthStats;
    childCount?: number;
    instrument?: string;
  };
  currentBotType?: 'shadow' | 'original';
}

// Minimal decorative background (removed AnimatedParticles for performance)
const SimpleBackground: React.FC = React.memo(() => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
    <div className="absolute top-1/4 right-1/4 w-8 h-8 border border-primary/10 rounded-full" />
  </div>
));

// Optimized mini sparkline component with memoization
const MiniSparkline: React.FC<{data: number[], color: string}> = React.memo(({ data, color }) => {
  const sparklineData = useMemo(() => {
    if (!data.length) return null;
    
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min;
    
    const points = data.map((value, index) => {
      const x = (index / (data.length - 1)) * 100;
      const y = range === 0 ? 50 : 100 - ((value - min) / range) * 100;
      return `${x},${y}`;
    }).join(' ');
    
    return points;
  }, [data]);

  const strokeColor = useMemo(() => {
    return color === 'success' ? '#22c55e' : color === 'destructive' ? '#ef4444' : 'hsl(var(--primary))';
  }, [color]);
  
  if (!sparklineData) return null;
  
  return (
    <div className="w-12 h-6 opacity-40">
      <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
        <polyline
          fill="none"
          stroke={strokeColor}
          strokeWidth="2"
          points={sparklineData}
          className="opacity-60"
        />
      </svg>
    </div>
  );
});

export const PropBotGrowthChart: React.FC<PropBotGrowthChartProps> = ({
  originalData,
  shadowData,
  originalStats,
  shadowStats,
  linkedBotType,
  minCapital,
  isSimulatedData = false,
  isMerged = false,
  mergedData,
  currentBotType = 'original'
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState(() => {
    // Always show the better performing bot first (higher final profit)
    return originalStats.finalDollarAmount >= shadowStats.finalDollarAmount ? 'original' : 'shadow';
  });
  const [zoomDomain, setZoomDomain] = useState<{left?: number; right?: number} | null>(null);
  const [chartMode, setChartMode] = useState<'area' | 'line' | 'volume'>('area');
  const [animationProgress, setAnimationProgress] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [useCanvas, setUseCanvas] = useState(isMerged); // Default to canvas for merged data
  // Always use merged when available for user view
  const analysisMode = isMerged ? 'merged' : 'individual';
  const animationRef = useRef<number>();
  
  // Zoom functionality states
  const [refAreaLeft, setRefAreaLeft] = useState<number | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<number | null>(null);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');

  // Memory manager for performance optimization
  const memoryManager = useMemo(() => MemoryManager.getInstance({
    maxMemoryMB: 128,
    cleanupThreshold: 0.7,
    chunkSize: 500
  }), []);

  // Simplified animation (removed for performance)
  useEffect(() => {
    setAnimationProgress(100);
    setIsAnimating(false);
  }, [activeTab]);

  // Cleanup memory on unmount
  useEffect(() => {
    return () => {
      memoryManager.destroy();
    };
  }, [memoryManager]);

  // Process data for chart rendering with optimized Y-axis domain
  const { chartData, filteredChartData, profitData, lossData, yAxisDomain } = useMemo(() => {
    // Use merged data if merged mode is selected and available, otherwise use individual data
    const rawData = analysisMode === 'merged' && isMerged && mergedData 
      ? mergedData.growthData 
      : (activeTab === 'original' ? originalData : shadowData);
    
    // Transform data to account values and add timestamp processing for merged data
    const chartData = rawData.map((point, index) => {
      const accountValue = point.dollarAmount + minCapital;
      const timestampMs = point.timestamp ? new Date(point.timestamp).getTime() : undefined;
      
      return {
        ...point,
        accountValue,
        index: index + 1,
        tradeNumber: index + 1,
        timestampMs // Add for time-based X-axis
      };
    });

    // Filter data based on zoom domain
    const filteredChartData = zoomDomain 
      ? chartData.filter(d => d.tradeNumber >= (zoomDomain.left || 1) && d.tradeNumber <= (zoomDomain.right || chartData.length))
      : chartData;

    // Calculate optimal Y-axis domain using filtered data
    const values = filteredChartData.map(d => d.accountValue);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const dataRange = maxValue - minValue;
    
    // Different domain logic for zoom vs full view
    let yAxisDomain: [number, number];
    
    if (zoomDomain) {
      // When zoomed: focus only on filtered data range for better readability
      const paddingPercent = dataRange < 1000 ? 0.15 : 0.1;
      const padding = dataRange * paddingPercent;
      const domainMin = minValue - padding;
      const domainMax = maxValue + padding;
      yAxisDomain = [Math.max(0, domainMin), domainMax];
    } else {
      // When full view: ensure minCapital line is visible
      const paddingPercent = dataRange < 1000 ? 0.15 : 0.1;
      const padding = dataRange * paddingPercent;
      const domainMin = Math.min(minValue - padding, minCapital - padding);
      const domainMax = Math.max(maxValue + padding, minCapital + padding);
      yAxisDomain = [Math.max(0, domainMin), domainMax];
    }

    // Create separate datasets for profit/loss visualization using filtered data
    const profitData = filteredChartData.map(point => 
      point.accountValue >= minCapital 
        ? { ...point, value: point.accountValue }
        : { ...point, value: null }
    );

    const lossData = filteredChartData.map(point => 
      point.accountValue < minCapital 
        ? { ...point, value: point.accountValue }
        : { ...point, value: null }
    );

    return { chartData, filteredChartData, profitData, lossData, yAxisDomain };
  }, [activeTab, originalData, shadowData, minCapital, zoomDomain, isMerged, mergedData, analysisMode]);

  // Determine which stats to use based on analysis mode
  const currentStats = analysisMode === 'merged' && isMerged && mergedData 
    ? mergedData.stats 
    : (activeTab === 'original' ? originalStats : shadowStats);

  // Stats data with enhanced design and progress bars
  const stats = useMemo(() => {
    // Add safety checks for currentStats properties
    const finalAmount = currentStats?.finalDollarAmount ?? 0;
    const maxAmount = currentStats?.maxDollarAmount ?? 0;
    const maxDrawdown = currentStats?.maxDrawdownDollar ?? 0;
    const winRate = currentStats?.winRate ?? 0;
    
    return [
      {
        label: 'Final Profit',
        value: `${finalAmount >= 0 ? '+' : ''}$${Math.abs(finalAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
        icon: DollarSign,
        color: finalAmount >= 0 ? 'success' : 'destructive',
        highlight: true,
        progress: Math.min(Math.abs(finalAmount) / (minCapital * 0.5) * 100, 100),
        sparkline: chartData.slice(-10).map(d => d?.dollarAmount ?? 0)
      },
      {
        label: 'Peak Profit',
        value: `$${maxAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
        icon: TrendingUp,
        color: 'success',
        highlight: false,
        progress: Math.min(maxAmount / (minCapital * 0.3) * 100, 100),
        sparkline: chartData.map(d => d?.accountValue ?? 0).slice(-8)
      },
      {
        label: 'Max Drawdown',
        value: `$${maxDrawdown.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
        icon: TrendingDown,
        color: 'destructive',
        highlight: false,
        progress: Math.min(Math.abs(maxDrawdown) / (minCapital * 0.2) * 100, 100),
        sparkline: chartData.map(d => Math.min(0, d?.dollarAmount ?? 0)).slice(-8)
      },
      {
        label: 'Win Rate',
        value: `${winRate.toFixed(1)}%`,
        icon: Target,
        color: 'primary',
        highlight: false,
        progress: winRate,
        sparkline: chartData.slice(-10).map(d => d?.actual === 1 ? 1 : 0)
      }
    ];
  }, [currentStats, chartData, minCapital]);

  const getColorClasses = (color: string, highlight?: boolean) => {
    const baseClasses = highlight 
      ? 'bg-gradient-to-br border shadow-lg hover:shadow-xl transition-all duration-500 hover:scale-[1.02] backdrop-blur-sm'
      : 'bg-gradient-to-br border shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.01] backdrop-blur-sm';
    
    switch (color) {
      case 'success':
        return `${baseClasses} from-green-500/15 via-green-500/8 to-green-500/5 border-green-500/30 hover:shadow-green-500/20 hover:from-green-500/20`;
      case 'destructive':
        return `${baseClasses} from-red-500/15 via-red-500/8 to-red-500/5 border-red-500/30 hover:shadow-red-500/20 hover:from-red-500/20`;
      case 'primary':
        return `${baseClasses} from-primary/15 via-primary/8 to-primary/5 border-primary/30 hover:shadow-primary/20 hover:from-primary/20`;
      default:
        return `${baseClasses} from-muted/15 via-muted/8 to-muted/5 border-border/30`;
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload as GrowthDataPoint & { accountValue: number; timestampMs?: number };
    
    // Add null/undefined checks
    if (!data || data.dollarAmount === undefined || data.dollarAmount === null) return null;
    
    const previousIndex = chartData.findIndex(d => d.tradeNumber === data.tradeNumber) - 1;
    const previousData = previousIndex >= 0 ? chartData[previousIndex] : null;
    
    // Ensure we have valid numbers with fallbacks
    const currentAmount = data.dollarAmount || 0;
    const previousAmount = previousData?.dollarAmount || 0;
    
    const tradePNL = previousData 
      ? currentAmount - previousAmount
      : currentAmount;
    
    const totalPNL = currentAmount;
    const percentagePNL = minCapital > 0 ? (totalPNL / minCapital) * 100 : 0;
    const result = data.actual === 1 ? 'WIN' : 'LOSS';
    
    return (
      <div className="bg-background/95 backdrop-blur-sm border border-border rounded-lg shadow-lg p-3 min-w-48">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-3 w-3 text-primary" />
          <p className="font-medium text-sm text-foreground">Trade #{label}</p>
        </div>
        <div className="space-y-2 text-xs">
          {/* Show Date/Time for merged data */}
          {analysisMode === 'merged' && data.timestamp && (
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Date/Time:</span>
              <span className="font-medium text-xs">
                {new Date(data.timestamp).toLocaleString()}
              </span>
            </div>
          )}
          {/* Show Instrument for merged data - prefer per-point instrument over run-level */}
          {analysisMode === 'merged' && (data.instrument || mergedData?.instrument) && (
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Instrument:</span>
              <span className="font-medium text-xs">{data.instrument || mergedData?.instrument}</span>
            </div>
          )}
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Result:</span>
            <Badge variant={result === 'WIN' ? 'default' : 'destructive'} className="text-xs px-1.5 py-0.5">
              {result}
            </Badge>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Trade PNL:</span>
            <span className={`font-medium ${tradePNL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {tradePNL >= 0 ? '+' : ''}${(tradePNL || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Total PNL:</span>
            <span className={`font-medium ${totalPNL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {totalPNL >= 0 ? '+' : ''}${(totalPNL || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">% PNL:</span>
            <span className={`font-medium ${percentagePNL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {percentagePNL >= 0 ? '+' : ''}{(percentagePNL || 0).toFixed(2)}%
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Zoom functionality handlers
  const handleMouseDown = useCallback((e: any) => {
    if (e && e.activeLabel) {
      setRefAreaLeft(e.activeLabel);
      setRefAreaRight(e.activeLabel);
    }
  }, []);

  const handleMouseMove = useCallback((e: any) => {
    if (refAreaLeft && e && e.activeLabel) {
      setRefAreaRight(e.activeLabel);
    }
  }, [refAreaLeft]);

  const handleMouseUp = useCallback(() => {
    if (refAreaLeft && refAreaRight && refAreaLeft !== refAreaRight) {
      const left = Math.min(refAreaLeft, refAreaRight);
      const right = Math.max(refAreaLeft, refAreaRight);
      setZoomDomain({ left, right });
    }
    setRefAreaLeft(null);
    setRefAreaRight(null);
  }, [refAreaLeft, refAreaRight]);

  const handleZoomFromDropdown = useCallback((count: number) => {
    if (chartData.length === 0) return;
    
    if (count === -1) {
      // Show all trades
      setZoomDomain(null);
    } else {
      // Show last N trades
      const totalTrades = chartData.length;
      const startIndex = Math.max(1, totalTrades - count + 1);
      const endIndex = totalTrades;
      setZoomDomain({ left: startIndex, right: endIndex });
    }
  }, [chartData.length]);

  const handleQuickZoom = useCallback((e: React.MouseEvent, count: number) => {
    e.preventDefault();
    e.stopPropagation();
    handleZoomFromDropdown(count);
  }, [handleZoomFromDropdown]);

  const handleManualRange = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const start = parseInt(rangeStart);
    const end = parseInt(rangeEnd);
    
    // Allow partial input - at least one valid number
    if (!isNaN(start) || !isNaN(end)) {
      const finalStart = !isNaN(start) ? start : 1;
      const finalEnd = !isNaN(end) ? end : chartData.length;
      
      if (finalStart > 0 && finalEnd > finalStart && finalEnd <= chartData.length) {
        setZoomDomain({ left: finalStart, right: finalEnd });
      }
    }
  }, [rangeStart, rangeEnd, chartData.length]);

  const handleZoomOut = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setZoomDomain(null);
  }, []);

  const handleReset = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setZoomDomain(null);
    setRangeStart('');
    setRangeEnd('');
  }, []);

  // Add Enter key support for manual range
  const handleRangeKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleManualRange(e as any);
    }
  }, [handleManualRange]);

  return (
    <Card className="w-full bg-background border border-border shadow-lg relative">
      {/* Simple background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.01] to-transparent" />
      
      <SimpleBackground />
      
      <CardHeader className="relative p-3 md:p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Activity className="h-4 w-4 text-primary" />
            </div>
            <div>
              <CardTitle className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-foreground flex items-center gap-2`}>
                {t('growth_chart')}
                {currentBotType === 'shadow' && (
                  <>
                    <Badge variant="secondary" className="text-xs px-2 py-0.5">Shadow</Badge>
                    <Badge variant="outline" className="text-xs px-2 py-0.5">{t('reverse_positions')}</Badge>
                  </>
                )}
                {mergedData?.instrument && (
                  <Badge variant="outline" className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border-blue-200">
                    {mergedData.instrument}
                  </Badge>
                )}
              </CardTitle>
              {!isMobile && (
                <CardDescription className="text-xs text-muted-foreground">
                  Track bot performance with detailed profit/loss analysis
                </CardDescription>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            
            {/* Chart mode switcher */}
            <div className="flex items-center gap-1 bg-background/20 backdrop-blur-sm rounded-lg p-1 border border-border/30">
              {[
                { mode: 'area' as const, icon: BarChart3, label: 'Area' },
                { mode: 'line' as const, icon: LineChartIcon, label: 'Line' },
                { mode: 'volume' as const, icon: Volume2, label: 'Volume' }
              ].map(({ mode, icon: Icon, label }) => (
                <Button
                  key={mode}
                  variant={chartMode === mode ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setChartMode(mode)}
                  className={`h-8 px-2 transition-all duration-200 ${
                    chartMode === mode 
                      ? 'bg-primary/20 text-primary shadow-sm' 
                      : 'hover:bg-background/40 text-muted-foreground'
                  }`}
                >
                  <Icon className="h-3 w-3" />
                  {!isMobile && <span className="ml-1 text-xs">{label}</span>}
                </Button>
              ))}
            </div>
            
            <div className="flex items-center gap-2">
              {!isMerged && isSimulatedData && (
                <Badge variant="outline" className="text-xs bg-gradient-to-r from-blue-500/10 to-blue-500/5 border-blue-500/40 text-blue-500 shadow-sm backdrop-blur-sm">
                  <Activity className="h-3 w-3 mr-1 animate-pulse" />
                  Projected Data
                </Badge>
              )}
            </div>
          </div>
        </div>
        
        {/* Animation progress bar */}
        <div className="mt-4 w-full h-1 bg-muted/20 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-primary via-blue-500 to-primary rounded-full transition-all duration-300 ease-out"
            style={{ width: `${animationProgress}%` }}
          />
        </div>
      </CardHeader>
      
      <CardContent className="p-3 md:p-4 space-y-4">
        {/* Bot Type Tabs - Only show if not merged */}
        {!isMerged && (
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'original' | 'shadow')} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-muted/20 p-1 rounded-lg h-8">
              <TabsTrigger value="original" className="relative transition-all duration-200 data-[state=active]:bg-background data-[state=active]:shadow-sm text-sm">
                Original Bot
                {linkedBotType === 'original' && (
                  <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-primary rounded-full" />
                )}
              </TabsTrigger>
              <TabsTrigger value="shadow" className="relative transition-all duration-200 data-[state=active]:bg-background data-[state=active]:shadow-sm text-sm">
                Shadow Bot
                {linkedBotType === 'shadow' && (
                  <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-primary rounded-full" />
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        

          <div className="space-y-4 mt-4">
            {/* Compact Stats Cards */}
            <div className={`grid ${isMobile ? 'grid-cols-2 gap-2' : 'grid-cols-2 lg:grid-cols-4 gap-3'}`}>
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div
                    key={index}
                    className={`${isMobile ? 'p-2' : 'p-3'} rounded-lg ${getColorClasses(stat.color, stat.highlight)} relative overflow-hidden group`}
                  >
                    <div className="relative z-10">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className={`${isMobile ? 'p-1' : 'p-1.5'} rounded ${
                            stat.color === 'success' ? 'bg-green-500/20 text-green-500' :
                            stat.color === 'destructive' ? 'bg-red-500/20 text-red-500' :
                            stat.color === 'primary' ? 'bg-primary/20 text-primary' :
                            'bg-muted/20 text-muted-foreground'
                          }`}>
                            <Icon className={`${isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-muted-foreground font-medium mb-0.5`}>
                              {stat.label}
                            </p>
                            <p className={`${isMobile ? 'text-xs' : 'text-sm'} font-bold ${
                              stat.color === 'success' ? 'text-green-500' :
                              stat.color === 'destructive' ? 'text-red-500' :
                              stat.color === 'primary' ? 'text-primary' :
                              'text-foreground'
                            }`}>
                              {stat.value}
                            </p>
                          </div>
                        </div>
                        
                        {/* Mini sparkline - hide on mobile for space */}
                        {!isMobile && (
                          <MiniSparkline 
                            data={stat.sparkline || []} 
                            color={stat.color}
                          />
                        )}
                      </div>
                      
                      {/* Compact progress bar */}
                      {stat.progress !== undefined && (
                        <div className="w-full h-1.5 bg-muted/20 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all duration-500 ${
                              stat.color === 'success' ? 'bg-green-500' :
                              stat.color === 'destructive' ? 'bg-red-500' :
                              'bg-primary'
                            }`}
                            style={{ width: `${Math.min(stat.progress, 100)}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Responsive Zoom Controls */}
            <div className="relative z-50 bg-muted/20 p-3 rounded-lg border border-border/50 pointer-events-auto">
              {isMobile ? (
                /* Mobile: Compact single row layout */
                 <div className="flex items-center gap-2 flex-wrap">
                   {/* Range Inputs */}
                  <div className="flex items-center gap-1">
                    <Target className="h-3 w-3 text-muted-foreground" />
                    <Input
                      type="number"
                      placeholder="1"
                      value={rangeStart}
                      onChange={(e) => setRangeStart(e.target.value)}
                      onKeyPress={handleRangeKeyPress}
                      className="w-12 h-7 text-xs border-border/40 bg-background/50"
                      min="1"
                      max={chartData.length}
                    />
                    <span className="text-xs text-muted-foreground">-</span>
                    <Input
                      type="number"
                      placeholder={chartData.length.toString()}
                      value={rangeEnd}
                      onChange={(e) => setRangeEnd(e.target.value)}
                      onKeyPress={handleRangeKeyPress}
                      className="w-12 h-7 text-xs border-border/40 bg-background/50"
                      min="1"
                      max={chartData.length}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleManualRange}
                      className="h-7 px-1.5 text-xs border-border/40 hover:bg-primary/10 hover:border-primary/30 transition-all duration-200"
                    >
                      Go
                    </Button>
                  </div>

                  {/* Compact Stats */}
                  <div className="flex items-center gap-2 ml-auto text-xs">
                    <span className="text-muted-foreground">{currentStats.totalTrades}T</span>
                    <span className="text-green-500">{currentStats.wins}W</span>
                    <span className="text-red-500">{currentStats.losses}L</span>
                  </div>

                  {/* Reset */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReset}
                    className="h-7 px-2 text-xs border-border/40 hover:bg-destructive/10 hover:border-destructive/30 transition-all duration-200"
                  >
                    <RotateCcw className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                /* Desktop: Original layout */
                 <div className="flex flex-wrap items-center gap-3">
                   {/* Custom Range Controls */}
                  <div className="flex items-center gap-2 pointer-events-auto">
                    <span className="text-xs text-muted-foreground pointer-events-none">Range:</span>
                    <Input
                      type="number"
                      placeholder="1"
                      value={rangeStart}
                      onChange={(e) => setRangeStart(e.target.value)}
                      onKeyPress={handleRangeKeyPress}
                      className="w-16 h-8 text-xs pointer-events-auto cursor-text"
                      min="1"
                      max={chartData.length}
                    />
                    <span className="text-xs text-muted-foreground pointer-events-none">-</span>
                    <Input
                      type="number"
                      placeholder={chartData.length.toString()}
                      value={rangeEnd}
                      onChange={(e) => setRangeEnd(e.target.value)}
                      onKeyPress={handleRangeKeyPress}
                      className="w-16 h-8 text-xs pointer-events-auto cursor-text"
                      min="1"
                      max={chartData.length}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleManualRange(e);
                      }}
                      disabled={!rangeStart && !rangeEnd}
                      className="h-8 px-3 text-xs pointer-events-auto cursor-pointer hover:bg-primary/10 hover:border-primary/40 transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ZoomIn className="h-3 w-3" />
                    </Button>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-3 ml-auto">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Activity className="h-3 w-3" />
                      <span>{currentStats.totalTrades} trades</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                      <span className="text-green-500">{currentStats.wins}W</span>
                      <span className="text-red-500">{currentStats.losses}L</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Compact Chart Container */}
            {chartData.length > 0 ? (
              <div className={`${isMobile ? 'h-[400px]' : 'h-[400px]'} w-full ${isMobile ? 'p-1' : 'p-4'} bg-background rounded-lg border border-border overflow-hidden relative`}>
                <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.01] to-transparent" />
                
                 {analysisMode === 'merged' && useCanvas ? (
                    <CanvasEquityChart
                      data={chartData.map((point, index) => {
                        const previousPoint = index > 0 ? chartData[index - 1] : null;
                        const tradePnl = previousPoint ? (point.dollarAmount || 0) - (previousPoint.dollarAmount || 0) : (point.dollarAmount || 0);
                        
                        return {
                          timestamp: point.timestamp || new Date(Date.now() + index * 60000).toISOString(),
                          cumulative_pnl: point.dollarAmount || 0,
                          total_pnl: tradePnl,
                          bot_contributions: {},
                          instrument: point.instrument, // Pass per-point instrument to CanvasEquityChart
                          originalIndex: index
                        };
                      })}
                      title="Equity Curve"
                      chartMode={chartMode === 'area' ? 'area' : 'line'}
                      hideSubtitle
                      instrument={mergedData?.instrument}
                    />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                  {chartMode === 'volume' ? (
                     <BarChart 
                      data={filteredChartData} 
                       margin={isMobile ? { top: 10, right: 15, left: 25, bottom: 10 } : { top: 15, right: 15, left: 10, bottom: 20 }}
                       onMouseDown={handleMouseDown}
                       onMouseMove={handleMouseMove}
                       onMouseUp={handleMouseUp}
                     >
                       <defs>
                         <linearGradient id="volumeGradient" x1="0" y1="0" x2="0" y2="1">
                           <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.8} />
                           <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.1} />
                         </linearGradient>
                       </defs>
                       <CartesianGrid 
                         strokeDasharray="2 4" 
                         stroke="hsl(var(--border))" 
                         opacity={0.15}
                         horizontal={true}
                         vertical={false}
                       />
                        <XAxis 
                          dataKey={isMerged ? "timestampMs" : "tradeNumber"}
                          domain={zoomDomain ? [zoomDomain.left, zoomDomain.right] : ['dataMin', 'dataMax']}
                          type="number"
                          scale={isMerged ? "time" : "linear"}
                          tick={{ fontSize: isMobile ? 9 : 11, fill: 'hsl(var(--muted-foreground))', fontWeight: 500 }}
                          axisLine={{ stroke: 'hsl(var(--border))', strokeWidth: 1, opacity: 0.5 }}
                          tickLine={false}
                          tickMargin={8}
                          interval={isMobile ? 'preserveStartEnd' : 'preserveEnd'}
                          tickCount={isMobile ? 3 : undefined}
                          tickFormatter={isMerged ? (value) => {
                            try {
                              return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                            } catch {
                              return String(value);
                            }
                          } : undefined}
                        />
                        <YAxis 
                         domain={yAxisDomain}
                         tickFormatter={(value) => `${isMobile ? '$' + (value / 1000).toFixed(0) + 'k' : '$' + (value / 1000).toFixed(0) + 'k'}`}
                         tick={{ fontSize: isMobile ? 9 : 11, fill: 'hsl(var(--muted-foreground))', fontWeight: 500 }}
                         axisLine={false}
                         tickLine={false}
                         tickMargin={8}
                         width={isMobile ? 25 : 50}
                         tickCount={isMobile ? 4 : undefined}
                       />
                      <Tooltip content={<CustomTooltip />} />
                      
                      {refAreaLeft && refAreaRight && (
                        <ReferenceArea
                          x1={refAreaLeft}
                          x2={refAreaRight}
                          strokeOpacity={0.3}
                          fillOpacity={0.1}
                          fill="hsl(var(--primary))"
                          stroke="hsl(var(--primary))"
                        />
                      )}
                      
                      <Bar
                        dataKey="dollarAmount"
                        fill="url(#volumeGradient)"
                        radius={[2, 2, 0, 0]}
                      />
                    </BarChart>
                  ) : chartMode === 'area' ? (
                     <AreaChart 
                      data={filteredChartData} 
                       margin={isMobile ? { top: 10, right: 15, left: 25, bottom: 10 } : { top: 15, right: 15, left: 10, bottom: 20 }}
                       onMouseDown={handleMouseDown}
                       onMouseMove={handleMouseMove}
                       onMouseUp={handleMouseUp}
                     >
                       <defs>
                         <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                           <stop offset="0%" stopColor="hsl(142 76% 36%)" stopOpacity={0.4} />
                           <stop offset="50%" stopColor="hsl(142 76% 36%)" stopOpacity={0.2} />
                           <stop offset="100%" stopColor="hsl(142 76% 36%)" stopOpacity={0.02} />
                         </linearGradient>
                         <linearGradient id="lossGradient" x1="0" y1="0" x2="0" y2="1">
                           <stop offset="0%" stopColor="hsl(0 84% 60%)" stopOpacity={0.4} />
                           <stop offset="50%" stopColor="hsl(0 84% 60%)" stopOpacity={0.2} />
                           <stop offset="100%" stopColor="hsl(0 84% 60%)" stopOpacity={0.02} />
                         </linearGradient>
                          <linearGradient id="profitStroke" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="hsl(142 86% 46%)" />
                            <stop offset="100%" stopColor="hsl(142 76% 36%)" />
                          </linearGradient>
                          <linearGradient id="lossStroke" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="hsl(0 94% 70%)" />
                            <stop offset="100%" stopColor="hsl(0 84% 60%)" />
                          </linearGradient>
                       </defs>
                       <CartesianGrid 
                         strokeDasharray="2 4" 
                         stroke="hsl(var(--border))" 
                         opacity={0.15}
                         horizontal={true}
                         vertical={false}
                       />
                         <XAxis 
                          dataKey={isMerged ? "timestampMs" : "tradeNumber"}
                          domain={zoomDomain ? [zoomDomain.left, zoomDomain.right] : ['dataMin', 'dataMax']}
                          type="number"
                          scale={isMerged ? "time" : "linear"}
                          tick={{ fontSize: isMobile ? 9 : 11, fill: 'hsl(var(--muted-foreground))', fontWeight: 500 }}
                          axisLine={{ stroke: 'hsl(var(--border))', strokeWidth: 1, opacity: 0.5 }}
                          tickLine={false}
                          tickMargin={8}
                          interval={isMobile ? 'preserveStartEnd' : 'preserveEnd'}
                          tickCount={isMobile ? 3 : undefined}
                          tickFormatter={isMerged ? (value) => {
                            try {
                              return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                            } catch {
                              return String(value);
                            }
                          } : undefined}
                        />
                       <YAxis 
                         domain={yAxisDomain}
                         tickFormatter={(value) => `${isMobile ? '$' + (value / 1000).toFixed(0) + 'k' : '$' + (value / 1000).toFixed(0) + 'k'}`}
                         tick={{ fontSize: isMobile ? 9 : 11, fill: 'hsl(var(--muted-foreground))', fontWeight: 500 }}
                         axisLine={false}
                         tickLine={false}
                         tickMargin={8}
                         width={isMobile ? 25 : 50}
                         tickCount={isMobile ? 4 : undefined}
                       />
                      <Tooltip 
                        content={<CustomTooltip />} 
                        cursor={{ 
                          strokeDasharray: '4 4', 
                          stroke: 'hsl(var(--primary))', 
                          strokeOpacity: 0.3,
                          strokeWidth: 2
                        }} 
                      />
                      
                      {refAreaLeft && refAreaRight && (
                        <ReferenceArea
                          x1={refAreaLeft}
                          x2={refAreaRight}
                          strokeOpacity={0.3}
                          fillOpacity={0.1}
                          fill="hsl(var(--primary))"
                          stroke="hsl(var(--primary))"
                        />
                      )}
                      
                      <ReferenceLine 
                        y={minCapital} 
                        stroke="hsl(var(--muted-foreground))" 
                        strokeDasharray="8 4" 
                        strokeOpacity={0.6}
                        strokeWidth={2}
                      />
                      
                      <Area
                        data={profitData}
                        type="monotoneX"
                        dataKey="value"
                        stroke="none"
                        fill="url(#profitGradient)"
                        connectNulls={false}
                        fillOpacity={1}
                      />
                      
                      <Area
                        data={lossData}
                        type="monotoneX"
                        dataKey="value"
                        stroke="none"
                        fill="url(#lossGradient)"
                        connectNulls={false}
                        fillOpacity={1}
                      />
                      
                       <Area
                         type="monotoneX"
                         dataKey="accountValue"
                         stroke={currentStats.finalDollarAmount >= 0 ? "url(#profitStroke)" : "url(#lossStroke)"}
                         strokeWidth={4}
                         fill="none"
                         connectNulls={false}
                         className={`transition-all duration-1000 ease-out ${isAnimating ? 'animate-[drawLine_1s_ease-out_forwards]' : ''}`}
                         style={{
                           strokeDasharray: isAnimating ? '0 1000' : undefined,
                           '--line-length': '1000'
                         } as any}
                       />
                    </AreaChart>
                  ) : (
                     <LineChart 
                      data={filteredChartData} 
                       margin={isMobile ? { top: 10, right: 15, left: 25, bottom: 10 } : { top: 15, right: 15, left: 10, bottom: 20 }}
                       onMouseDown={handleMouseDown}
                       onMouseMove={handleMouseMove}
                       onMouseUp={handleMouseUp}
                     >
                       <defs>
                         <linearGradient id="profitStroke" x1="0" y1="0" x2="1" y2="0">
                           <stop offset="0%" stopColor="hsl(142 86% 46%)" />
                           <stop offset="100%" stopColor="hsl(142 76% 36%)" />
                         </linearGradient>
                         <linearGradient id="lossStroke" x1="0" y1="0" x2="1" y2="0">
                           <stop offset="0%" stopColor="hsl(0 94% 70%)" />
                           <stop offset="100%" stopColor="hsl(0 84% 60%)" />
                         </linearGradient>
                       </defs>
                       <CartesianGrid 
                         strokeDasharray="2 4" 
                         stroke="hsl(var(--border))" 
                         opacity={0.15}
                         horizontal={true}
                         vertical={false}
                       />
                         <XAxis 
                          dataKey={isMerged ? "timestampMs" : "tradeNumber"}
                          domain={zoomDomain ? [zoomDomain.left, zoomDomain.right] : ['dataMin', 'dataMax']}
                          type="number"
                          scale={isMerged ? "time" : "linear"}
                          tick={{ fontSize: isMobile ? 9 : 11, fill: 'hsl(var(--muted-foreground))', fontWeight: 500 }}
                          axisLine={{ stroke: 'hsl(var(--border))', strokeWidth: 1, opacity: 0.5 }}
                          tickLine={false}
                          tickMargin={8}
                          interval={isMobile ? 'preserveStartEnd' : 'preserveEnd'}
                          tickCount={isMobile ? 3 : undefined}
                          tickFormatter={isMerged ? (value) => {
                            try {
                              return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                            } catch {
                              return String(value);
                            }
                          } : undefined}
                        />
                       <YAxis 
                         domain={yAxisDomain}
                         tickFormatter={(value) => `${isMobile ? '$' + (value / 1000).toFixed(0) + 'k' : '$' + (value / 1000).toFixed(0) + 'k'}`}
                         tick={{ fontSize: isMobile ? 9 : 11, fill: 'hsl(var(--muted-foreground))', fontWeight: 500 }}
                         axisLine={false}
                         tickLine={false}
                         tickMargin={8}
                         width={isMobile ? 25 : 50}
                         tickCount={isMobile ? 4 : undefined}
                       />
                      <Tooltip 
                        content={<CustomTooltip />} 
                        cursor={{ 
                          strokeDasharray: '4 4', 
                          stroke: 'hsl(var(--primary))', 
                          strokeOpacity: 0.3,
                          strokeWidth: 2
                        }} 
                      />
                      
                      {refAreaLeft && refAreaRight && (
                        <ReferenceArea
                          x1={refAreaLeft}
                          x2={refAreaRight}
                          strokeOpacity={0.3}
                          fillOpacity={0.1}
                          fill="hsl(var(--primary))"
                          stroke="hsl(var(--primary))"
                        />
                      )}
                      
                      <ReferenceLine 
                        y={minCapital} 
                        stroke="hsl(var(--muted-foreground))" 
                        strokeDasharray="8 4" 
                        strokeOpacity={0.6}
                        strokeWidth={2}
                      />
                      
                       <Line
                         type="monotoneX"
                         dataKey="accountValue"
                         stroke={currentStats.finalDollarAmount >= 0 ? "url(#profitStroke)" : "url(#lossStroke)"}
                         strokeWidth={4}
                         dot={false}
                         activeDot={{ 
                           r: 6, 
                           fill: "hsl(var(--primary))", 
                           stroke: "hsl(var(--background))", 
                           strokeWidth: 2
                         }}
                         connectNulls={false}
                         className={`transition-all duration-1000 ease-out ${isAnimating ? 'animate-[drawLine_1s_ease-out_forwards]' : ''}`}
                         style={{
                           strokeDasharray: isAnimating ? '0 1000' : undefined,
                           '--line-length': '1000'
                         } as any}
                       />
                     </LineChart>
                   )}
                 </ResponsiveContainer>
                )}
               </div>
            ) : (
              <div className="h-[500px] flex items-center justify-center relative bg-gradient-to-br from-muted/10 to-muted/5 rounded-2xl border border-border/30">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_hsl(var(--primary)/0.05),transparent_70%)]" />
                <div className="text-center relative z-10">
                  <div className="relative">
                    <Activity className="h-16 w-16 mx-auto mb-6 opacity-30" />
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary/20 rounded-full animate-ping" />
                  </div>
                  <p className="text-xl font-bold mb-3 text-foreground/80">🚀 Ready for Launch</p>
                  <p className="text-sm opacity-60 max-w-sm mx-auto leading-relaxed">
                    Your trading journey will be visualized here with beautiful charts and real-time insights once trading begins
                  </p>
                  <div className="mt-6 flex justify-center">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground bg-background/50 px-3 py-1 rounded-full border border-border/30">
                      <Sparkles className="h-3 w-3" />
                      Enhanced Analytics Ready
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>
      </CardContent>
    </Card>
  );
};